import {
    refreshSession,
    toSession,
    type AuthCall,
    type TokenBundle,
} from './api';
import { loadSession, saveSession } from './storage';

// Two layers of coordination:
//   1. In-memory dedupe (`inFlight`): if the same JS context fires multiple
//      refresh attempts for the same token, they all await one promise.
//   2. Web Locks (`navigator.locks`): exclusive same-origin lock so two tabs
//      / PWA contexts can't both fire POST /auth/refresh with the same
//      refresh token.  Without this, the second request hits the server's
//      reuse-detection path (the first already rotated the token) and the
//      server revokes every refresh token for the user — kicking every
//      device off at once.
//
//   Inside the lock we re-read IDB: if another tab refreshed while we were
//   waiting, we adopt the freshly stored bundle instead of calling /refresh
//   ourselves.  We also persist the new bundle BEFORE releasing the lock so
//   the next waiter sees it.

type Flight = {
    token: string;
    promise: Promise<AuthCall<TokenBundle>>;
};

let inFlight: Flight | null = null;

export function refreshSingleFlight(
    refreshToken: string,
): Promise<AuthCall<TokenBundle>> {
    if (inFlight && inFlight.token === refreshToken) {
        return inFlight.promise;
    }
    const promise = (async () => {
        try {
            return await refreshUnderLock(refreshToken);
        } finally {
            if (inFlight?.token === refreshToken) inFlight = null;
        }
    })();
    inFlight = { token: refreshToken, promise };
    return promise;
}

async function refreshUnderLock(
    myToken: string,
): Promise<AuthCall<TokenBundle>> {
    if (!('locks' in navigator)) return doRefresh(myToken);
    return navigator.locks.request(
        'seneca-refresh',
        { mode: 'exclusive' },
        () => doRefresh(myToken),
    );
}

async function doRefresh(myToken: string): Promise<AuthCall<TokenBundle>> {
    const stored = await loadSession();
    if (stored && stored.refresh_token !== myToken) {
        // Another tab already rotated the token while we waited on the lock.
        // Reuse its result; calling /refresh with our stale token here would
        // trip server-side reuse-detection and kill every device.
        return {
            ok: true,
            value: {
                access_token: stored.access_token,
                refresh_token: stored.refresh_token,
            },
        };
    }
    const result = await refreshSession(myToken);
    if (result.ok) await saveSession(toSession(result.value));
    return result;
}
